import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Input, Modal, Select, Skeleton, useToast } from '../../../design-system';
import ServicePicker from './ServicePicker';
import {
  computeTotals, annualValue, money, basisLabel, FREQUENCY_LABEL, BILLING_DAYS, billingDayLabel,
} from '../../../lib/retainer';
import '../../styles/Retainers.css';

// The one editor for a retainer, rendered by BOTH the Retainers section and
// Finance's Retainers tab — the PlanChooser rule. Two screens for one
// arrangement drifted the last time this codebase tried it, and the half that
// lost was the one people actually used.
//
// ⚠️ THE TOTALS SHOWN HERE COME FROM lib/retainer.js, THE SERVER'S MIRROR, not
// from arithmetic written into this component. What the firm sees while it
// negotiates must be the figure that ends up on the invoice; a sum computed
// inline is a second source of truth for the number a client signs.

const FREQ = Object.entries(FREQUENCY_LABEL).map(([value, label]) => ({ value, label }));

const todayISO = () => new Date().toISOString().slice(0, 10);

function RetainerEditor({ retainer = {}, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !retainer.id;

  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState({ services: [], currency: 'INR' });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  // ⚠️ AN EXISTING RETAINER IS RE-FETCHED, NEVER TRUSTED FROM THE ROW THAT
  // OPENED IT. Finance's list endpoint returns a summary — no `services`, no
  // `scope` — and this form posts every field it holds, so opening from that
  // tab and pressing Save would have written an EMPTY service list over a real
  // one and re-priced the agreement to zero. The two hosts hand over an id;
  // the record comes from one place.
  const [hydrating, setHydrating] = useState(!isNew);
  // Whether the agreed figure is in play at all. Derived from the stored row so
  // reopening a discounted retainer shows the discount rather than silently
  // resetting it to the list price.
  const [negotiated, setNegotiated] = useState(retainer.agreedAmount != null);

  const [form, setForm] = useState({
    clientId: retainer.clientId || '',
    title: retainer.title || 'Retainer',
    services: retainer.services || [],
    agreedAmount: retainer.agreedAmount ?? '',
    amount: retainer.amount ?? '',
    frequency: retainer.frequency || 'monthly',
    billingDay: retainer.billingDay || 1,
    kind: retainer.kind || 'professional_fees',
    startDate: (retainer.startDate || new Date().toISOString()).slice(0, 10),
    endDate: retainer.endDate ? retainer.endDate.slice(0, 10) : '',
    agreementDate: retainer.agreementDate ? retainer.agreementDate.slice(0, 10) : todayISO(),
    isActive: retainer.isActive !== false,
    autoIssue: !!retainer.autoIssue,
    scope: retainer.scope || '',
    paymentTerms: retainer.paymentTerms || '',
    notes: retainer.notes || '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isNew) {
      apiClient.get('/api/clients/options')
        .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data?.clients || [])))
        .catch(() => setClients([]));
    }
    apiClient.get('/api/retainers/service-options')
      .then((r) => setCatalog({ services: r.data.services || [], currency: r.data.currency || 'INR' }))
      .catch(() => setCatalog({ services: [], currency: 'INR' }));

    if (!isNew) {
      apiClient.get(`/api/retainers/${retainer.id}`)
        .then((r) => {
          const full = r.data.retainer;
          setNegotiated(full.agreedAmount != null);
          setForm({
            clientId: full.clientId,
            title: full.title || 'Retainer',
            services: full.services || [],
            agreedAmount: full.agreedAmount ?? '',
            amount: full.amount ?? '',
            frequency: full.frequency || 'monthly',
            billingDay: full.billingDay || 1,
            kind: full.kind || 'professional_fees',
            startDate: (full.startDate || new Date().toISOString()).slice(0, 10),
            endDate: full.endDate ? full.endDate.slice(0, 10) : '',
            agreementDate: full.agreementDate ? full.agreementDate.slice(0, 10) : todayISO(),
            isActive: full.isActive !== false,
            autoIssue: !!full.autoIssue,
            scope: full.scope || '',
            paymentTerms: full.paymentTerms || '',
            notes: full.notes || '',
          });
        })
        .catch((e) => toast.danger(e.response?.data?.error || 'Could not load that retainer'))
        .finally(() => setHydrating(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const totals = useMemo(() => computeTotals({
    services: form.services,
    agreedAmount: negotiated && form.agreedAmount !== '' ? Number(form.agreedAmount) : null,
    fallbackAmount: Number(form.amount) || 0,
  }), [form.services, form.agreedAmount, form.amount, negotiated]);

  const chosenIds = useMemo(
    () => new Set(form.services.map((s) => s.serviceId).filter(Boolean)),
    [form.services]
  );

  const addService = (s) => {
    set('services', [...form.services, {
      serviceId: s.id,
      name: s.name,
      groupName: s.groupName,
      description: '',
      // Snapshotted, not referenced — see the note on the model. Both figures
      // start equal; the quoted one is then the firm's to move.
      listAmount: s.price?.amount ?? null,
      billingBasis: s.price?.billingBasis || 'per_filing',
      kind: s.price?.kind || 'professional_fees',
      amount: s.price?.amount ?? 0,
    }]);
  };

  const addCustom = (name) => {
    set('services', [...form.services, {
      serviceId: null,
      name,
      groupName: 'Other services',
      description: '',
      listAmount: null,
      billingBasis: 'per_filing',
      kind: 'professional_fees',
      amount: 0,
    }]);
  };

  const setLine = (i, patch) => set('services', form.services.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  const removeLine = (i) => set('services', form.services.filter((_, n) => n !== i));

  const payload = () => ({
    ...(isNew ? { clientId: form.clientId } : {}),
    title: form.title,
    services: form.services,
    // ⚠️ Sent as null, not omitted, when the discount is switched off — the
    // server reads `undefined` as "leave it alone", so omitting it would keep
    // billing an old agreed figure after somebody unticked the box.
    agreedAmount: negotiated && form.agreedAmount !== '' ? Number(form.agreedAmount) : null,
    amount: form.services.length ? undefined : Number(form.amount),
    frequency: form.frequency,
    billingDay: Number(form.billingDay) || 1,
    kind: form.kind,
    startDate: form.startDate,
    endDate: form.endDate || null,
    agreementDate: form.agreementDate || null,
    isActive: form.isActive,
    autoIssue: form.autoIssue,
    scope: form.scope,
    paymentTerms: form.paymentTerms,
    notes: form.notes,
  });

  const save = async () => {
    if (isNew && !form.clientId) { toast.danger('Choose a client'); return null; }
    if (!form.services.length && !Number(form.amount)) {
      toast.danger('Add a service, or enter an amount'); return null;
    }
    setSaving(true);
    try {
      const r = await apiClient.put(`/api/retainers/${retainer.id || 'new'}`, payload());
      toast.success('Retainer saved');
      return r.data.retainer;
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
      return null;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save, then file the agreement into the client's documents.
   *
   * ⚠️ SAVED FIRST, ALWAYS. The document is a rendering of the stored row, so
   * generating from an unsaved form would file a paper that says something the
   * database does not — and it is the paper the client is handed.
   */
  const saveAndGenerate = async () => {
    const saved = await save();
    if (!saved) return;
    setGenerating(true);
    try {
      const r = await apiClient.post(`/api/retainers/${saved.id}/agreement`);
      const { status, reason } = r.data;
      if (status === 'filed') toast.success("Agreement filed in the client's documents");
      else if (status === 'superseded') toast.success(`Agreement updated (v${r.data.retainer.agreementVersion})`);
      else if (status === 'skipped') toast.info(`Not filed — ${reason}`);
      else toast.danger(`Could not file the agreement — ${reason || 'unknown error'}`);
      onSaved?.(r.data.retainer);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not generate the agreement');
    } finally {
      setGenerating(false);
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Stop and remove this retainer? Invoices already raised are kept.')) return;
    setSaving(true);
    try {
      await apiClient.delete(`/api/retainers/${retainer.id}`);
      toast.success('Retainer removed');
      onSaved?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not remove');
    } finally {
      setSaving(false);
    }
  };

  // Nothing may be saved or generated until the real record has arrived —
  // a Save fired against a half-loaded form writes the placeholder over it.
  const busy = saving || generating || hydrating;
  const cur = catalog.currency;
  const per = FREQUENCY_LABEL[form.frequency].toLowerCase();

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={isNew ? 'New retainer' : `${retainer.agreementNumber || 'Retainer'} — ${retainer.clientName}`}
      footer={(
        <>
          {/* Closing is always available: a slow fetch must not trap somebody in a
              dialog they opened by mistake. */}
          <Button variant="secondary" onClick={onClose} disabled={saving || generating}>Cancel</Button>
          {!isNew && <Button variant="secondary" onClick={remove} disabled={busy}>Remove</Button>}
          <Button variant="secondary" onClick={saveAndGenerate} loading={generating} disabled={saving || hydrating}>
            Save &amp; generate agreement
          </Button>
          <Button
            variant="primary"
            onClick={async () => { const s = await save(); if (s) onSaved?.(s); }}
            loading={saving}
            disabled={generating || hydrating}
          >
            Save
          </Button>
        </>
      )}
    >
      {hydrating ? <Skeleton.Text lines={10} /> : (
      <div className="ret-form">
        <div className="ret-form-row">
          {isNew ? (
            <Select label="Client" value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
              <option value="">— choose —</option>
              {clients.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>
                  {c.displayName || c.name}{c.clientCode ? ` (${c.clientCode})` : ''}
                </option>
              ))}
            </Select>
          ) : (
            <Input label="Client" value={retainer.clientName || ''} readOnly disabled />
          )}
          <Input label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
          <Select label="Billed" value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
            {FREQ.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </Select>
        </div>

        {/* ── What the retainer covers ── */}
        <div className="ret-section">
          <h4 className="ret-h">Services covered</h4>
          <p className="ret-sub">
            Rates come from the firm&apos;s service catalog and are copied onto this agreement — changing
            the catalog later will not re-price it. Every fee here is per {per.replace('every ', '')}.
          </p>

          {form.services.length > 0 && (
            <table className="ret-lines">
              <thead>
                <tr>
                  <th>Service</th>
                  <th style={{ width: 150 }}>Head</th>
                  <th className="ret-num" style={{ width: 130 }}>Fee</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {form.services.map((s, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${s.serviceId || 'custom'}-${i}`}>
                    <td>
                      <div className="ret-line-name">{s.name}</div>
                      <div className="ret-line-meta">
                        {s.groupName}
                        {s.listAmount != null && ` · standard ${money(s.listAmount, cur)} ${basisLabel(s.billingBasis)}`}
                      </div>
                      <input
                        className="ret-line-desc"
                        placeholder="What this covers (optional, prints on the agreement)"
                        value={s.description}
                        onChange={(e) => setLine(i, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <select
                        className="ret-line-select"
                        value={s.kind}
                        onChange={(e) => setLine(i, { kind: e.target.value })}
                      >
                        <option value="professional_fees">Professional fees</option>
                        <option value="ope">Reimbursement (no GST)</option>
                      </select>
                    </td>
                    <td className="ret-num">
                      <input
                        className="ret-line-amount"
                        type="number"
                        min="0"
                        // ⚠️ step="any" — a number input defaults to step=1 and
                        // the browser then marks a fee with paise invalid.
                        step="any"
                        value={s.amount}
                        onChange={(e) => setLine(i, { amount: e.target.value === '' ? 0 : Number(e.target.value) })}
                      />
                    </td>
                    <td>
                      <button type="button" className="ret-line-x" onClick={() => removeLine(i)} aria-label={`Remove ${s.name}`}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <ServicePicker
            services={catalog.services}
            chosenIds={chosenIds}
            onAdd={addService}
            onAddCustom={addCustom}
            currency={cur}
          />
        </div>

        {/* ── What it costs ── */}
        <div className="ret-section">
          <h4 className="ret-h">The fee</h4>

          {form.services.length === 0 ? (
            <>
              <div className="ret-form-row">
                <Input
                  label={`Amount (${per})`}
                  type="number"
                  min="0"
                  step="any"
                  value={form.amount}
                  onChange={(e) => set('amount', e.target.value)}
                />
                <Select label="Head" value={form.kind} onChange={(e) => set('kind', e.target.value)}>
                  <option value="professional_fees">Professional fees (taxable)</option>
                  <option value="ope">Reimbursement (no GST)</option>
                </Select>
              </div>
              <p className="ret-sub">
                A flat fee with no services listed. Add services above and the fee is built from them instead.
              </p>
            </>
          ) : (
            <>
              <div className="ret-totals">
                <div className="ret-total-row">
                  <span>Services listed</span>
                  <b>{money(totals.listTotal, cur)}</b>
                </div>

                <label className="ret-toggle">
                  <input
                    type="checkbox"
                    checked={negotiated}
                    onChange={(e) => {
                      setNegotiated(e.target.checked);
                      // Prefilled with the list price so the field opens on the
                      // number being negotiated down FROM, not on a blank box.
                      if (e.target.checked && form.agreedAmount === '') set('agreedAmount', totals.listTotal);
                    }}
                  />
                  <span>
                    <b>We agreed a different figure</b>
                    <small>
                      A package quoted at {money(totals.listTotal, cur)} and settled at less. The agreed
                      figure is what invoices — the list price stays on record so the concession is visible
                      here and on the agreement.
                    </small>
                  </span>
                </label>

                {negotiated && (
                  <div className="ret-total-row ret-agreed">
                    <span>Agreed fee, {per.replace('every ', 'per ')}</span>
                    <input
                      className="ret-line-amount"
                      type="number"
                      min="0"
                      step="any"
                      value={form.agreedAmount}
                      onChange={(e) => set('agreedAmount', e.target.value)}
                    />
                  </div>
                )}

                {totals.discount !== 0 && (
                  <div className={`ret-total-row ${totals.discount > 0 ? 'ret-disc' : 'ret-prem'}`}>
                    <span>{totals.discount > 0 ? 'Concession' : 'Premium'}{totals.discountPct ? ` (${Math.abs(totals.discountPct)}%)` : ''}</span>
                    <b>{totals.discount > 0 ? '−' : '+'}{money(Math.abs(totals.discount), cur)}</b>
                  </div>
                )}

                <div className="ret-total-row ret-final">
                  <span>Bills {per.toLowerCase()}</span>
                  <b>{money(totals.amount, cur)}</b>
                </div>
                <div className="ret-total-row ret-muted">
                  <span>Over a full year</span>
                  <span>{money(annualValue(totals.amount, form.frequency), cur)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Term and the paper ── */}
        <div className="ret-section">
          <h4 className="ret-h">Term &amp; billing day</h4>

          <div className="ret-form-row">
            <Select
              label="Raise the invoice on"
              value={form.billingDay}
              onChange={(e) => set('billingDay', Number(e.target.value))}
            >
              {BILLING_DAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </Select>
            <Input label="Starts" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            <Input label="Ends (optional)" type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            <Input label="Agreement dated" type="date" value={form.agreementDate} onChange={(e) => set('agreementDate', e.target.value)} />
          </div>

          {/* The default is the 1st, which is what nearly every firm does. This
              says what the chosen day actually means — "the 5th" is ambiguous
              on its own between the period and the month it is billed in. */}
          <p className="ret-sub">
            {billingDayLabel(form.billingDay, form.frequency)}.
            {Number(form.billingDay) > 28 && ' Shorter months bill on their last day.'}
          </p>

          {/* The design system's Input renders an <input>; a multi-line field
              uses the raw element with the same class, as InvoiceBuilder does. */}
          <div className="ds-field">
            <label htmlFor="ret-scope" className="ds-field-label">Scope (prints on the agreement)</label>
            <textarea
              id="ret-scope"
              className="ds-input"
              rows="3"
              placeholder="What is included, and anything explicitly outside it."
              value={form.scope}
              onChange={(e) => set('scope', e.target.value)}
            />
          </div>
          <Input
            label="Payment terms (optional)"
            value={form.paymentTerms}
            onChange={(e) => set('paymentTerms', e.target.value)}
            placeholder="e.g. Payable within 15 days of the invoice date."
          />
          <Input
            label="Internal notes"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Not shown to the client unless you generate the agreement."
          />

          <label className="ret-toggle">
            <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
            <span><b>Active</b><small>Switch off to stop generating invoices. Nothing already raised is affected.</small></span>
          </label>
          <label className="ret-toggle">
            <input type="checkbox" checked={form.autoIssue} onChange={(e) => set('autoIssue', e.target.checked)} />
            <span>
              <b>Issue invoices automatically</b>
              <small>
                Off by default — the invoice is created as a draft for you to check. Issuing consumes a
                statutory invoice number that cannot be reused.
              </small>
            </span>
          </label>

          <p className="ret-sub">
            Billed in arrears: the invoice for a period is generated once that period has finished, so you
            never raise a document for work not yet done.
            {form.services.length > 0 && ' Each service appears as its own line, with the agreed figure spread across them.'}
          </p>
        </div>
      </div>
      )}
    </Modal>
  );
}

export default RetainerEditor;
